import {
  Button,
  FieldWrapper,
  Icons,
  Label,
  useToggle,
} from '@commenda/kakashi';
import { THumanReviewStepDefinition, TValueProvider } from '@commenda/types';
import { TMapping } from '@commenda/types/src/workflow/dsl';
import { useState } from 'react';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';
import { ValueProviderEditor } from '../editors/ValueProviderEditor';
import { getDefaultFieldRefObj } from '../editors/helpers';
import { MappingModal } from '../modals/AddEditMappingModal';

export const HumanReviewNodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow } = useWorkflow();

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as THumanReviewStepDefinition | undefined;

  const [mappingToEdit, setMappingToEdit] = useState<TMapping | null>(null);
  const addEditMappingModalVisibility = useToggle(false);

  if (!selectedStep) return <div>Step not found</div>;

  const updateData = (data: TValueProvider) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id ? { ...step, data } : step
      ),
    });
  };

  const updateStep = (updates: Partial<THumanReviewStepDefinition>) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id ? { ...step, ...updates } : step
      ),
    });
  };

  const onSaveContextProjectionMapping = (mapping: TMapping) => {
    const existing = selectedStep.contextProjection?.find(
      (m) => m.id === mapping.id
    );
    if (existing) {
      updateStep({
        contextProjection: selectedStep.contextProjection?.map((m) =>
          m.id === mapping.id ? mapping : m
        ),
      });
    } else {
      updateStep({
        contextProjection: [
          ...(selectedStep.contextProjection || []),
          mapping,
        ],
      });
    }
  };

  const precedingSteps = workflow?.steps.filter(
    (step) => step.id !== selectedEntity.id
  );

  return (
    <div className="flex flex-col gap-4">
      <FieldWrapper>
        <Label>Data to Review</Label>
        <ValueProviderEditor
          value={selectedStep.data ?? getDefaultFieldRefObj()}
          onChange={updateData}
          steps={precedingSteps ?? []}
          dataSources={[]}
        />
      </FieldWrapper>

      <FieldWrapper>
        <Label className="flex justify-between items-center">
          <span className="grow">Context Projection</span>
          <Button
            size="sm"
            onClick={() => {
              setMappingToEdit(null);
              addEditMappingModalVisibility.show();
            }}
          >
            <Icons.Plus className="size-4" />
          </Button>
        </Label>
        <div className="flex flex-col gap-2">
          {selectedStep.contextProjection?.map((mapping) => (
            <div
              key={mapping.id}
              className="flex items-center justify-between gap-2"
            >
              <span>{mapping.targetPath}</span>
              <div className="flex items-center gap-2">
                <Icons.Edit01
                  className="size-4"
                  onClick={() => {
                    setMappingToEdit(mapping);
                    addEditMappingModalVisibility.show();
                  }}
                />
                <Icons.Trash02
                  className="size-4"
                  onClick={() => {
                    updateStep({
                      contextProjection:
                        selectedStep.contextProjection?.filter(
                          (m) => m.id !== mapping.id
                        ),
                    });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </FieldWrapper>

      <MappingModal
        isOpen={addEditMappingModalVisibility.visible}
        onClose={() => {
          addEditMappingModalVisibility.hide();
          setMappingToEdit(null);
        }}
        mapping={mappingToEdit}
        onMappingChange={onSaveContextProjectionMapping}
        workflowDefinition={workflow}
        currentStepId={selectedEntity.id}
      />
    </div>
  );
};
